import {Injectable, InjectionToken} from '@angular/core';
import {environment} from "../../../../../../environments/environment";
import {AuthService} from "../../../../auth/api/service/auth.service";
import {BehaviorSubject, ReplaySubject, Subject} from "rxjs";
import {AiRealtimeToolDefinitionRegistry} from "../../impl/service/aiRealtimeToolDefinition.registry";

export class AiRealtimeServiceOptions {
    name: string;
    mute? = false;
}

export type AiRealtimeToolStatus =
    | 'running'
    | 'success'
    | 'error';

export interface AiRealtimeToolItem {
    type: 'tool';
    callId: string;
    toolName: string;
    status: AiRealtimeToolStatus;
    title: string;
    appEvents?: AiRealtimeAppEvent[];
}

export interface AiRealtimeToolDefinition {
    toolName: string;
    runningLabel: string;
    errorLabel: string;
    successLabel: string;
    appEventNames: string[];
}

export const AI_REALTIME_TOOL_DEFINITIONS =
    new InjectionToken<AiRealtimeToolDefinition[]>('AI_REALTIME_TOOL_DEFINITIONS');

export interface AiRealtimeAppEvent {
    type: 'app.event';
    name: string;
    data: any;
}


@Injectable({ providedIn: 'root' })
export class AIRealtimeService {
    private pc?: RTCPeerConnection;
    private dc?: RTCDataChannel;
    private micStream?: MediaStream;
    private remoteAudioEl?: HTMLAudioElement;
    private options: AiRealtimeServiceOptions;
    private logs: string[] = [];

    readonly connected$ = new BehaviorSubject<boolean>(false);

    private readonly toolItemsSubject = new BehaviorSubject<AiRealtimeToolItem[]>([]);
    readonly toolItems$ = this.toolItemsSubject.asObservable();

    constructor(private authService: AuthService,
                private toolDefinitionRegistry: AiRealtimeToolDefinitionRegistry) {
    }

    private upsertToolItem(item: AiRealtimeToolItem): void {
        const items = this.toolItemsSubject.value;
        const index = items.findIndex(x => x.callId === item.callId);

        if (index === -1) {
            this.toolItemsSubject.next([...items, item]);
            return;
        }

        this.toolItemsSubject.next([
            ...items.slice(0, index),
            { ...items[index], ...item },
            ...items.slice(index + 1),
        ]);
    }

    attachAudioElement(el: HTMLAudioElement | null) {
        this.remoteAudioEl = el ?? undefined;
        // Si on a déjà un stream distant, on le (re)branche
        if (this.remoteAudioEl && this.pc) {
            const senders = this.pc.getReceivers();
        }
    }




    async connect(context: Record<string, any>, options: AiRealtimeServiceOptions): Promise<void> {
        this.logs = [];
        this.log('connect() start', { context });
        this.options = options;
        this.toolItemsSubject.next([]);

        if (this.pc && this.dc) {
            this.log('Already connected/reusing existing PeerConnection');
        }

        this.pc = new RTCPeerConnection();
        this.log('RTCPeerConnection created');

        this.pc.onconnectionstatechange = () => {
            this.log('connectionState', this.pc?.connectionState, 'debug');
        };

        this.pc.oniceconnectionstatechange = () => {
            this.log('iceConnectionState', this.pc?.iceConnectionState);
        };

        this.pc.onsignalingstatechange = () => {
            this.log('signalingState:', this.pc?.signalingState, 'debug');
        };

        this.pc.onicegatheringstatechange = () => {
            this.log('iceGatheringState:', this.pc?.iceGatheringState, 'debug');
        };

        this.pc.onicecandidate = (event) => {
            if (event.candidate) {
                this.log('ICE candidate:', event.candidate.candidate, 'debug');
            } else {
                this.log('ICE gathering complete', undefined, 'debug');
            }
        };

        this.log('Requesting microphone...', undefined, 'debug');
        this.micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        this.log('Microphone stream acquired', {
            tracks: this.micStream.getTracks().map(t => ({
                kind: t.kind,
                label: t.label,
                enabled: t.enabled,
                readyState: t.readyState
            }))
        });

        this.micStream.getTracks().forEach(t => {
            this.log('Adding local track:' + t.kind + '-' + t.label, undefined, 'debug');
            this.pc!.addTrack(t, this.micStream!);
        });

        this.pc.addTransceiver('audio', { direction: 'recvonly' });
        this.log('Added recvonly audio transceiver', undefined, 'debug');

        this.pc.ontrack = (e) => {
            this.log('Remote track received', {
                kind: e.track.kind,
                id: e.track.id,
                streams: e.streams.length
            }, 'debug');

            if (this.remoteAudioEl && e.streams[0]) {
                this.log('Attaching remote stream to audio element', undefined,'debug');
                this.remoteAudioEl.srcObject = e.streams[0];
                this.remoteAudioEl.play()
                    .then(() => this.log('Remote audio playback started', undefined, 'debug'))
                    .catch(err => this.log('Remote audio play failed', err, 'warn'));
            } else {
                this.log('Remote track received but no audio element or stream available', undefined, 'warn');
            }
        };

        this.dc = this.pc.createDataChannel('oai-events');
        this.log('DataChannel created', undefined, 'debug');

        this.dc.onopen = () => {
            this.log('DataChannel OPEN', undefined, 'debug');
        };

        this.dc.onclose = () => {
            this.log('DataChannel CLOSED', undefined, 'debug');
        };

        this.dc.onerror = (err) => {
            this.log('DataChannel ERROR', err, 'error');
        };

        this.dc.onmessage = async (e) => {
            this.log('Raw DataChannel message:', e.data);

            let payload: any = e.data;
            if (payload instanceof Blob) {
                payload = await payload.text();
                this.log('Blob message converted to text:', payload);
            }

            let msg: any = payload;
            try {
                msg = typeof payload === 'string' ? JSON.parse(payload) : payload;
            } catch (err) {
                this.log('Failed to parse message as JSON', err, 'error');
            }
            this.log('Parsed event:', msg);
            try {
                await this.handleToolCallEvent(msg);
            } catch (err) {
                this.log('handleToolCallEvent failed', err, 'error');
            }
        };

        this.log('Creating SDP offer...', undefined, 'debug');
        const offer = await this.pc.createOffer();
        this.log('SDP offer created', {
            type: offer.type,
            sdpLength: offer.sdp?.length
        }, 'debug');

        await this.pc.setLocalDescription(offer);
        this.log('Local description set', undefined, 'debug');

        this.log('Sending SDP offer to backend...', undefined, 'debug');
        const sdpResp = await fetch(environment.webserviceUrl + '/ai/realtime/call', {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${this.authService.tokenContentSnapshot?.token}`,
                'Content-Type': 'application/sdp',
                'X-Realtime-Name': options.name,
                'X-Realtime-Context': encodeURIComponent(JSON.stringify(context ?? {}))
            },
            body: offer.sdp || ''
        });

        this.log('[Realtime] Backend SDP response status:' + sdpResp.status + ' ' + sdpResp.statusText, 'debug');

        const answerSdp = await sdpResp.text();

        if (!sdpResp.ok) {
            this.log('[Realtime] Backend SDP error:', answerSdp, 'error');
            throw new Error(answerSdp);
        }

        this.log('[Realtime] SDP answer received', {
            length: answerSdp.length,
            preview: answerSdp.substring(0, 200)
        }, 'debug');

        await this.pc.setRemoteDescription({
            type: 'answer',
            sdp: answerSdp
        });

        this.log('[Realtime] Remote description set', undefined, 'debug');
        this.connected$.next(true);
        this.log('[Realtime] connect() complete', undefined, 'debug');
    }

    /** Détecte les tool calls et orchestre l'exécution via le backend + renvoi au modèle */
    private async handleToolCallEvent(msg: any): Promise<void> {
        this.log('AI toolCall message received', msg);
        if (msg?.type !== 'response.function_call_arguments.done') return;
        const call_id = msg.call_id;
        const name = msg.name;
        const argStr = msg.arguments;

        if (!call_id || !name) return;

        let args: any = {};
        try {
            args = argStr ? JSON.parse(argStr) : {};
        } catch {
            args = {};
        }

        this.log('Trigger backend tool execution', {
            call_id: call_id,
            args: args
        });

        this.upsertToolItem({
            type: 'tool',
            callId: call_id,
            toolName: name,
            status: 'running',
            title: this.toolDefinitionRegistry.getRunningLabel(name),
        });

        const res = await fetch(environment.webserviceUrl + '/v1/ai/tool/execute', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${this.authService.tokenContentSnapshot?.token}`,
            },
            body: JSON.stringify({ call_id, name, args })
        });

        let result;
        let aiPayload;
        let inError = false;
        if (!res.ok) {
            inError = true;
            aiPayload = {
                commands: []
            };
        } else {
            const json = await res.json();
            result = json.result;
            aiPayload = result?.ai ?? {};

            inError =
                result?.status === 'technicalError' ||
                result?.status === 'businessError';
        }

        this.log('Tool execution result', result);

        if (inError) {
            this.log('[Realtime][Tool] business/technical error:', result, 'error');
            this.upsertToolItem({
                type: 'tool',
                callId: call_id,
                toolName: name,
                status: 'error',
                title: this.toolDefinitionRegistry.getErrorLabel(name),
            });

            this.sendFunctionCallOutput(call_id, undefined);
            this.sendResponseForAiCommands(
                aiPayload.commands?.length
                    ? aiPayload.commands
                    : [{
                        name: 'say',
                        data: {
                            text: "Une erreur est survenue pendant l'exécution de l'action."
                        }
                    }]
            );
            return;
        }

        const appEvents = this.extractAppEvents(result?.app);
        console.log('zzzz', appEvents);
        this.upsertToolItem({
            type: 'tool',
            callId: call_id,
            toolName: name,
            status: 'success',
            title: this.toolDefinitionRegistry.getSuccessLabel(name),
            appEvents,
        });

        this.sendFunctionCallOutput(call_id, aiPayload);

        this.sendResponseForAiCommands(aiPayload.commands);
    }

    private extractAppEvents(app: any): AiRealtimeAppEvent[] {
        if (!app) return [];

        const events = app.events ?? [];

        return events.map((event: any) => ({
            type: 'app.event',
            name: event.name,
            data: event.data
        }));
    }

    private sendResponseForAiCommands(commands: any[] | undefined): void {
        if (! commands) {
            return;
        }
        commands.forEach(command => {
            if (command?.name != 'silent') {
                if (command?.name != 'say' || !this.options.mute) {
                    const text = command?.data?.text;
                    if (text) {
                        this.sendToChannel({
                            type: 'response.create',
                            response: {
                                instructions: 'Dis: "' + text
                            }
                        });
                    }
                }
            }
        });
    }

    private sendToChannel(data: any) {
        this.log('[Realtime][sendToChannel]', data);
        this.dc?.send(JSON.stringify(data));
    }

    private sendFunctionCallOutput(call_id: string, aiPayload: any): void {
        const output = {
            memories: aiPayload?.memories ?? [],
        };
        this.sendToChannel({
            type: 'conversation.item.create',
            item: {
                type: 'function_call_output',
                call_id,
                output: JSON.stringify(output)
            }
        });
    }

    private log(description: string, data?: any, type: 'info' | 'debug' | 'error' | 'warn' = 'info') {
        const log = '[' + type + '][Realtime] ' + description;
        if (this.authService.tokenContentSnapshot?.superUser) {
            if (type == 'info') {
                console.log(log, data);
            } else if (type === 'warn') {
                console.warn(log, data);
            }else if (type === 'debug') {
                console.debug(log, data);
            } else if (type === 'error') {
                console.error(log, data);
            }
        }
        this.logs.push(log + (data ? (': ' + JSON.stringify(data)) : ''));
    }

    /** Envoie une requête de réponse avec audio + texte */
    speak(instructions: string) {
        if (!this.dc) throw new Error('Not connected');
        const event = {
            type: 'response.create',
            response: {
                modalities: ['audio', 'text'],
                instructions
            }
        };
        this.dc.send(JSON.stringify(event));
    }


    /** Toujours dispo si tu veux texte seul */
    sendPrompt(text: string) {
        if (!this.dc) throw new Error('Not connected');
        const event = {
            type: 'response.create',
            response: { modalities: ['text'], instructions: text }
        };
        this.dc.send(JSON.stringify(event));
    }


    disconnect() {
        this.dc?.close();
        this.pc?.close();
        this.micStream?.getTracks().forEach(t => t.stop());
        this.dc = undefined;
        this.pc = undefined;
        this.micStream = undefined;
        this.connected$.next(false);
    }
}

export class AiRealtimeEvent {
    type: string;
    name: string;
    data: any;
}